import React, { useCallback } from "react";
import { View, StyleSheet } from "react-native";
import { Shield } from "lucide-react";
import { InfoIcon } from "@phosphor-icons/react";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import { IconCircle, getIconCircleColor } from "./IconCircle";
import { ModerationFlag, AssignedUser, availableUsers } from "./types";
import ModerationFlagCard from "./ModerationFlagCard";
import { colors, spacing, radii, typographyScale } from "../../../styles/design-system";

interface ContentSafetySettingsProps {
  moderationSettings: ModerationFlag[];
  onSettingsChange?: (settings: ModerationFlag[]) => void;
}

function ContentSafetySettings({ onSettingsChange, moderationSettings }: ContentSafetySettingsProps) {
  const handleToggleFlag = useCallback(
    (flagId: string) => {
      const newSettings = moderationSettings.map((flag) =>
        flag.id === flagId ? { ...flag, enabled: !flag.enabled } : flag
      );
      onSettingsChange?.(newSettings);
    },
    [moderationSettings, onSettingsChange]
  );

  const handleAssignUser = useCallback(
    (flagId: string, userId: string | null) => {
      const newSettings = moderationSettings.map((flag) =>
        flag.id === flagId ? { ...flag, assignedUserId: userId } : flag
      );
      onSettingsChange?.(newSettings);
    },
    [moderationSettings, onSettingsChange]
  );

  const enabledCount = moderationSettings.filter((f) => f.enabled).length;
  const totalCount = moderationSettings.length;
  const unassignedCount = moderationSettings.filter((f) => f.enabled && !f.assignedUserId).length;

  const getAssignedUser = (userId: string | null): AssignedUser | null => {
    if (!userId) return null;
    return availableUsers.find((u) => u.id === userId) || null;
  };

  return (
    <View style={styles.container}>
      {/* Header Card */}
      <View style={styles.card}>
        <View style={styles.headerRow}>
          <IconCircle variant="teal" rounded="lg" icon={<Shield size={20} color={getIconCircleColor("teal")} />} />
          <View style={styles.headerText}>
            <CustomText
              text="Content Safety & Moderation"
              size="xxl"
              weight="bold"
              useTranslationText={false}
              style={styles.cardTitle}
            />
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              text="Configure AI content moderation to detect and flag potentially harmful content. Assign staff members to receive alerts when flagged content is detected."
              size="s"
              useTranslationText={false}
              style={styles.cardDescription}
            />
            <CustomSpacing type="vertical" size="s" />
          </View>
        </View>

        {/* Summary Stats */}
        <View style={styles.statsContainer}>
          <View style={styles.statItem}>
            <CustomText
              text={String(enabledCount)}
              size="xxl"
              weight="bold"
              useTranslationText={false}
              style={styles.statNumber}
            />
            <CustomText text="Active Flags" size="xs" useTranslationText={false} style={styles.statLabel} />
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statItem}>
            <CustomText
              text={String(totalCount)}
              size="xxl"
              weight="bold"
              useTranslationText={false}
              style={styles.statNumber}
            />
            <CustomText text="Total Available" size="xs" useTranslationText={false} style={styles.statLabel} />
          </View>
          <View style={styles.statDivider} />
          <View style={styles.statInfoContainer}>
            <View style={styles.infoRow}>
              <InfoIcon size={16} color="#3b82f6" weight="regular" />
              <CustomText
                text={
                  unassignedCount > 0
                    ? `${unassignedCount} enabled flag${unassignedCount > 1 ? "s" : ""} without an assigned reviewer.`
                    : "All enabled flags have assigned reviewers. Alerts will be sent to Safety & Support."
                }
                size="xs"
                useTranslationText={false}
                style={unassignedCount > 0 ? styles.warningText : styles.infoText}
              />
            </View>
          </View>
        </View>
      </View>

      <CustomSpacing type="vertical" size="xl" />

      {/* Moderation Flags */}
      {moderationSettings.map((flag) => (
        <React.Fragment key={flag.id}>
          <ModerationFlagCard
            flag={flag}
            assignedUser={getAssignedUser(flag.assignedUserId)}
            onToggle={() => handleToggleFlag(flag.id)}
            onAssignUser={(userId) => handleAssignUser(flag.id, userId)}
          />
          <CustomSpacing type="vertical" size="l" />
        </React.Fragment>
      ))}

      {/* Help Card */}
      <View style={styles.helpCard}>
        <InfoIcon size={20} color="rgb(7, 89, 133)" weight="regular" style={styles.helpIcon} />
        <View style={styles.helpContent}>
          <CustomText
            text="How Content Safety Works"
            size="m"
            weight="bold"
            useTranslationText={false}
            style={styles.helpTitle}
          />
          <CustomSpacing type="vertical" size="xs" />
          <View style={styles.helpList}>
            <CustomText
              text="• AI analyzes student conversations in real-time for potentially harmful content"
              size="s"
              useTranslationText={false}
              style={styles.helpListItem}
            />
            <CustomText
              text="• When flagged content is detected, the assigned reviewer is notified"
              size="s"
              useTranslationText={false}
              style={styles.helpListItem}
            />
            <CustomText
              text="• Notifications include context, student info, and a link to the conversation"
              size="s"
              useTranslationText={false}
              style={styles.helpListItem}
            />
            <CustomText
              text="• All flagged content is logged for review in Safety & Support"
              size="s"
              useTranslationText={false}
              style={styles.helpListItem}
            />
            <CustomText
              text="• Self-harm flags trigger immediate notifications for student welfare"
              size="s"
              useTranslationText={false}
              style={styles.helpListItem}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.lg,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  headerText: {
    flex: 1,
  },
  cardTitle: {
    color: colors.grey[900],
  },
  cardDescription: {
    color: colors.grey[600],
  },
  statsContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.grey[50],
    borderRadius: radii.lg,
    padding: 12,
    gap: spacing.lg,
  },
  statItem: {
    alignItems: "flex-start",
  },
  statNumber: {
    color: colors.grey[900],
  },
  statLabel: {
    color: colors.grey[500],
  },
  statDivider: {
    width: 1,
    height: 40,
    backgroundColor: colors.grey[200],
  },
  statInfoContainer: {
    flex: 1,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: spacing.xs,
  },
  infoText: {
    flex: 1,
    color: colors.grey[600],
  },
  warningText: {
    flex: 1,
    color: colors.warning[700],
  },
  helpCard: {
    backgroundColor: colors.blue[100],
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: colors.blue[500],
    padding: spacing.lg,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  helpIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  helpContent: {
    flex: 1,
  },
  helpTitle: {
    color: colors.grey[900],
  },
  helpList: {
    gap: 6,
  },
  helpListItem: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "400",
  },
});

export default ContentSafetySettings;
